import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { useApi } from "@platform/api";

import { Client } from "../types/clientTab";

interface UseClientOptions<TSelected> {
  clientId: string;
  enabled?: boolean;
  select?: (data: Client) => TSelected;
}

// Returns the React Query key for client data. Use this to reference client in cache
export const getClientQueryKey = (clientId: string): string[] => [
  "clients",
  clientId,
];

// useClient with selector overload
export function useClient<TSelected>(options: {
  clientId: string;
  enabled?: boolean;
  select: (data: Client) => TSelected;
}): UseQueryResult<TSelected, Error>;

// useClient with default select for all client data
export function useClient(options: {
  clientId: string;
  enabled?: boolean;
}): UseQueryResult<Client, Error>;

// useClient fetches client data given a client ID
// Pass in a React Query select to transform the data when reading from the cache (the original data is cached; select is applied on read)
export function useClient<TSelected = Client>({
  clientId,
  enabled = true,
  select,
}: UseClientOptions<TSelected>): UseQueryResult<TSelected, Error> {
  const axios = useApi();
  return useQuery({
    queryKey: getClientQueryKey(clientId),
    enabled: enabled && !!clientId,
    select,
    queryFn: () =>
      axios
        .get<Client>(`/brokerportal/v1/clients/${clientId}`)
        .then((res) => res.data),
  });
}
